import { useEffect, useMemo } from 'react';
import Head from 'next/head';
import { useSearchParams } from 'next/navigation';
import { isBrowser } from 'utils/dom';
import { urlCombine } from 'utils/url';

interface RedirectProps {
  url: string;
  permanent?: boolean;
}

export default function Redirect({ url, permanent }: RedirectProps) {
  const currentSearchParams = useSearchParams();
  const redirectUrl = useMemo(() => {
    if (!isBrowser) return url;

    const target = new URL(url, document.location.href);
    return target.origin === document.location.origin
      ? urlCombine(target, currentSearchParams).toString()
      : target.href;
  }, [url, currentSearchParams]);

  useEffect(() => {
    window.location.href = redirectUrl;
  }, [redirectUrl]);

  return (
    <>
      <Head>
        {permanent && (
          /**
           * Google respects http-equiv refresh with timeout 0 same as HTTP redirect 301.
           * @link https://developers.google.com/search/docs/crawling-indexing/301-redirects#metarefresh
           **/
          <meta
            http-equiv="refresh"
            content={`0;url=${redirectUrl}`}
          />
        )}
      </Head>
      <div className="fixed inset-0 bg-white" />
    </>
  );
}
